/**
 * formStyles.ts
 *
 * The single definition of form-control styling for the whole app.
 *
 * This started life inside PitchFormPage, where ~60 fields spread over several
 * large components had drifted field by field (labels alternated between
 * gray-700 and gray-900, inputs between two focus treatments). It now lives
 * here because the admin forms — /custom-subscription, /exclusive-permissions —
 * need exactly the same controls, and were each growing their own copy of these
 * strings. `PitchFormPage/components/ui/formStyles` re-exports this module, so
 * the original import path still works.
 *
 * Controls signal focus with a border-colour change only — no ring — matching
 * the inputs everywhere else in the app.
 */

import { buttonVariants } from "./buttonVariants";

/** Field label sitting above its control. */
export const formLabelClass = "block mb-2 text-sm font-medium text-neutral-700";

/** Text input / textarea. */
export const formInputClass =
  "w-full px-3 py-2 text-sm text-black bg-white border rounded-md outline-none transition-colors border-neutral-300 placeholder-neutral-400 focus:border-primary-500 disabled:bg-neutral-100 disabled:text-neutral-400 disabled:cursor-not-allowed";

/** Native <select>. Pairs with an absolutely-positioned chevron, hence pr-10. */
export const formSelectClass = `${formInputClass} appearance-none pr-10 cursor-pointer`;

/** Wrapper that positions the custom chevron for a native <select>. */
export const formSelectChevronClass =
  "absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none text-neutral-400";

/** Inline validation message under a field. */
export const formErrorClass = "mt-1 text-sm text-danger-600";

/** Helper text and character counters. */
export const formHelpClass = "mt-1 text-xs text-neutral-500";

/** Checkbox, matching the shared checkbox used across the app. */
export const formCheckboxClass =
  "w-4 h-4 rounded cursor-pointer text-primary-700 border-neutral-300 focus:border-primary-700";

/** Larger checkbox used for the prominent per-DSP flag cards. */
export const formCheckboxLargeClass =
  "w-5 h-5 mt-0.5 rounded cursor-pointer text-primary-700 border-neutral-300 focus:border-primary-700";

/** "+ Add …" inline actions. There are several on this page; they all use this. */
export const formAddItemClass =
  "border border-transparent inline-flex items-center gap-1 text-sm font-medium transition-colors rounded text-primary-700 hover:text-primary-800 hover:underline focus:outline-none focus-visible:border-primary-700";

/*
 * Button class strings, for a raw <button> or <Link> that can't become a
 * <Button>. All are built from buttonVariants, so they always match <Button>.
 * Prefer <Button variant="…"> in new code.
 */

/**
 * "+ Add …" button sitting beside a field label (Add New Artist, Add
 * Contributors, Add Platform-Specific UPC …). Smaller than a form action so it
 * doesn't compete with Submit, but the same size on every field.
 */
export const formAddButtonClass = buttonVariants({ variant: "soft", size: "sm" });

/**
 * Small action inside a table row (Edit, Add Artist …). Outline rather than
 * filled: a filled button repeated on every row drowns out the page's real
 * primary action.
 */
export const formRowActionButtonClass = buttonVariants({ variant: "secondary", size: "sm" });

/** Section card wrapping a group of fields. */
export const formSectionClass =
  "p-4 bg-white border rounded-lg border-neutral-200 sm:p-6";

/** Section heading inside a card. */
export const formSectionHeadingClass = "text-lg font-semibold text-black";

/**
 * Primary submit action.
 *
 * The disabled treatment is a neutral fill rather than a faded primary, so a
 * button that can't be submitted yet never reads as "a lighter shade of the
 * same button".
 */
export const formPrimaryButtonClass = buttonVariants({ variant: "primary" });

/** Secondary action (outline, brand-tinted). */
export const formSecondaryButtonClass = buttonVariants({ variant: "secondary" });

/**
 * Neutral action (outline, no brand tint). For Cancel / Back — the way out of a
 * form, which shouldn't compete with the brand-tinted secondary action.
 */
export const formNeutralButtonClass = buttonVariants({ variant: "neutral" });

/** Lowest-emphasis action (ghost). */
export const formGhostButtonClass = buttonVariants({ variant: "ghost" });

/** Starts a destructive action from a page (Delete Account, Remove). */
export const formDangerOutlineButtonClass = buttonVariants({ variant: "dangerOutline" });

/** Confirms a destructive action. */
export const formDangerButtonClass = buttonVariants({ variant: "danger" });
